import { useEffect, useRef, useState, Fragment } from 'react';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAllRows';
import { Search, Calendar, Check, X, ChevronDown, ChevronRight } from 'lucide-react';

interface LoadRow {
  id: string;
  load_number: string;
  invoice_number: string | null;
  pickup_date: string | null;
  customer_name: string | null;
  total_rate: number;
  notes: string | null;
  notes2: string | null;
  driver_id: string;
  driver_name: string;
  driver_pay: number | null;
  dispatch_id: string | null;
  settlement_net_pay: number | null;
  week_start: string;
  month_start: string;
  week_number: number;
  year: number;
  in_excel: boolean;
  excel_returned: boolean;
  carrier: string | null;
}

// Sentinel carrierFilter value for the bucket of loads whose carrier text
// matches no active company. Deliberately unlike any real company name.
const UNASSIGNED_CARRIER = '__unassigned__';

// `loads.carrier` is free text typed at booking time, so it drifts from
// `companies.name`: different casing, stray whitespace, or a legal suffix
// ("CS National" / "CS NATIONAL" / "CS National LLC" are all one carrier).
// Compare on a normalised key -- alphanumerics only, trailing entity suffix
// dropped -- and accept the company's name or its code, the same way
// ReadyForBillingView's companyForCarrier() matches loosely. A strict !==
// here dropped loads client-side that the server query had fetched. Module
// scope so the company-loading effect can reuse it.
const CARRIER_ENTITY_SUFFIXES = ['llc', 'inc', 'incorporated', 'corp', 'corporation', 'ltd', 'limited', 'llp', 'lp', 'plc', 'company', 'co'];
const normalizeCarrierKey = (value?: string | null) => {
  const tokens = (value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(' ').filter(Boolean);
  // Keep the last token when it is all the name has ("Co" as a company name).
  while (tokens.length > 1 && CARRIER_ENTITY_SUFFIXES.includes(tokens[tokens.length - 1])) {
    tokens.pop();
  }
  return tokens.join('');
};

// The invoice links in this view navigate the document (pushState + reload),
// and so do the load links in ReadyForBillingView, so this component gets
// remounted from scratch whenever the user comes back. Keep the carrier tab
// and the selected week in sessionStorage so they survive that, the same way
// LoadsView persists its filters. CompactLayout clears storage on sign-out.
const TABELA_VIEW_STATE_KEY = 'tabelaView:filterState';

type TabelaViewPersistedState = {
  carrierFilter: string;
  selectedWeek: string;
};

function readPersistedTabelaViewState(): Partial<TabelaViewPersistedState> {
  try {
    const raw = sessionStorage.getItem(TABELA_VIEW_STATE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

// PostgREST returns an embedded to-one relation as an object, but the same
// embed can come back as a single-element array, so accept both shapes.
type EmbeddedCompany = { name?: string | null };
function embeddedCompanyName(
  driver?: { companies?: EmbeddedCompany | EmbeddedCompany[] | null } | null
): string | null {
  const companies = driver?.companies;
  if (!companies) return null;
  return (Array.isArray(companies) ? companies[0]?.name : companies.name) || null;
}

export function TabelaView() {
  const persisted = readPersistedTabelaViewState();
  // Captured once at mount: the company load below must be able to tell a
  // restored selection from a cold start, and a ref keeps it out of the
  // effect's dependencies.
  const restoredCarrierRef = useRef(persisted.carrierFilter);
  const [loads, setLoads] = useState<LoadRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWeek, setSelectedWeek] = useState<string>(() => {
    // A restored week wins over the default, including the 'all' sentinel.
    if (persisted.selectedWeek) return persisted.selectedWeek;
    // Default to current week
    const now = new Date();
    const day = now.getDay();
    const diff = now.getDate() - day + (day === 0 ? -6 : 1);
    const weekStart = new Date(now);
    weekStart.setDate(diff);
    weekStart.setHours(0, 0, 0, 0);
    return weekStart.toISOString();
  });
  const [editingLoadId, setEditingLoadId] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<string>('');
  const [savingLoadId, setSavingLoadId] = useState<string | null>(null);
  const [editingDriverPayId, setEditingDriverPayId] = useState<string | null>(null);
  const [editingDriverPay, setEditingDriverPay] = useState<string>('');
  const [savingDriverPayId, setSavingDriverPayId] = useState<string | null>(null);
  const [editingNote2Id, setEditingNote2Id] = useState<string | null>(null);
  const [editingNote2, setEditingNote2] = useState<string>('');
  const [savingNote2Id, setSavingNote2Id] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<'week' | 'month'>('week');
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());
  const [loadsInExcel, setLoadsInExcel] = useState<Set<string>>(new Set());
  const [carrierFilter, setCarrierFilter] = useState<string>(persisted.carrierFilter ?? '');
  const [companies, setCompanies] = useState<Array<{id: string; name: string; code: string}>>([]);

  // Load companies from database
  useEffect(() => {
    async function loadCompanies() {
      const { data } = await supabase
        .from('companies')
        .select('id, name, code')
        .eq('is_active', true)
        .order('name');
      
      if (data && data.length > 0) {
        setCompanies(data);
        // Don't clobber a carrier restored from sessionStorage. It is stored by
        // name, so it only counts if it still matches an active company -- one
        // could have been renamed or deactivated since -- while the Unassigned
        // sentinel is a legitimate selection of its own. Otherwise seed the
        // first company, as before.
        const restored = restoredCarrierRef.current;
        const restoredIsValid =
          restored === UNASSIGNED_CARRIER ||
          (!!restored && data.some((c) => normalizeCarrierKey(c.name) === normalizeCarrierKey(restored)));
        if (!restoredIsValid) {
          setCarrierFilter(data[0].name);
        }
      }
    }
    loadCompanies();
  }, []);

  useEffect(() => {
    try {
      sessionStorage.setItem(TABELA_VIEW_STATE_KEY, JSON.stringify({
        carrierFilter,
        selectedWeek,
      }));
    } catch {
      // ignore quota / privacy-mode errors
    }
  }, [carrierFilter, selectedWeek]);

  useEffect(() => {
    loadTabelaData();
  }, []);

  async function loadTabelaData() {
    try {
      const data = await fetchAllRows<any>(() => supabase
        .from('loads')
        .select(`
          id,
          load_number,
          invoice_number,
          pickup_date,
          delivery_date,
          created_at,
          customer_name,
          total_rate,
          billing_note,
          billing_note2,
          excel_returned,
          carrier,
          driver_id,
          drivers (
            first_name,
            last_name,
            company_id,
            companies!drivers_company_id_fkey (
              name
            )
          ),
          dispatches!dispatches_load_id_fkey (
            id,
            driver_pay,
            dispatch_date,
            driver_id,
            drivers!dispatches_driver_id_fkey (
              first_name,
              last_name,
              company_id,
              companies!drivers_company_id_fkey (
                name
              )
            )
          ),
          load_stops (
            stop_type,
            date,
            stop_order
          )
        `)
        .not('load_number', 'ilike', '%REMOVED%')
        .order('pickup_date', { ascending: true }));

      console.log('📋 Tabela loaded loads:', data?.length);

      // Fetch Excel data to check which loads are in generated excels
      const { data: excelsData } = await supabase
        .from('generated_excels')
        .select('invoice_ids');
      
      const loadsInExcelSet = new Set<string>();
      (excelsData || []).forEach(excel => {
        if (excel.invoice_ids && Array.isArray(excel.invoice_ids)) {
          excel.invoice_ids.forEach(id => loadsInExcelSet.add(id));
        }
      });
      setLoadsInExcel(loadsInExcelSet);

      // Fetch settlement data for all drivers
      const driverIds = [...new Set((data || []).map((load: any) => load.driver_id))];
      const { data: settlementsData } = await supabase
        .from('driver_settlements')
        .select('driver_id, net_pay, period_start, period_end')
        .in('driver_id', driverIds)
        .eq('status', 'approved')
        .order('period_end', { ascending: false });

      const processedLoads: LoadRow[] = (data || []).map((load: any) => {
        // Get pickup date from load_stops if pickup_date field is null
        let actualPickupDate = load.pickup_date;
        
        if (!actualPickupDate && load.load_stops && load.load_stops.length > 0) {
          // First stop by order, preferring an explicit shipper. Note the app
          // writes stop_type as 'shipper' / 'consignee' / 'stop' - it never
          // writes 'pickup', so looking for that alone always missed and this
          // silently fell through to whatever stop happened to be first.
          const orderedStops = [...load.load_stops].sort((a: any, b: any) => a.stop_order - b.stop_order);
          const pickupStop = orderedStops.find((stop: any) => stop.stop_type === 'shipper')
            || orderedStops[0];

          if (pickupStop?.date) {
            actualPickupDate = pickupStop.date;
          }
        }

        // Date the row is grouped by. Falls back to created_at when the load
        // has neither a pickup_date nor a dated stop: using new Date() here
        // filed such a load under "the week you happen to be looking at", so it
        // silently moved to a different week every week and never settled into
        // a real one. created_at at least pins it to when it was entered.
        // Kept separate from actualPickupDate so the Pickup column stays empty
        // instead of displaying a creation date as if it were a pickup.
        const groupingDateSource = actualPickupDate || load.created_at;

        // Parse date purely in local timezone by extracting components directly
        // This avoids ANY timezone conversion issues
        let pickupDate: Date;
        if (groupingDateSource) {
          // Extract date components directly from the string (YYYY-MM-DD format)
          const dateStr = groupingDateSource.split('T')[0]; // Get just the date part
          const [yearStr, monthStr, dayStr] = dateStr.split('-');
          // Create date using local timezone constructor (month is 0-indexed)
          pickupDate = new Date(parseInt(yearStr), parseInt(monthStr) - 1, parseInt(dayStr), 0, 0, 0, 0);
        } else {
          pickupDate = new Date();
        }
        const weekStart = getWeekStart(pickupDate);
        const weekNumber = getWeekNumber(pickupDate);
        const year = pickupDate.getFullYear();
        
        // Extract dispatch info
        const dispatch = load.dispatches && load.dispatches.length > 0 ? load.dispatches[0] : null;
        const dispatchDriver = dispatch?.drivers;
        
        // Find the most recent settlement for this driver that covers this load's pickup date
        const driverSettlement = settlementsData?.find((s: any) =>
          s.driver_id === load.driver_id &&
          new Date(s.period_start) <= pickupDate &&
          new Date(s.period_end) >= pickupDate
        );
        
        // Extract driver_pay and dispatch_id from dispatches array (already extracted above)
        const driverPay = dispatch?.driver_pay || null;
        const dispatchId = dispatch?.id || null;
        
        const monthStart = getMonthStart(pickupDate);
        
        return {
          id: load.id,
          load_number: load.load_number,
          invoice_number: load.invoice_number,
          pickup_date: actualPickupDate,
          customer_name: load.customer_name,
          total_rate: load.total_rate,
          notes: load.billing_note,
          notes2: load.billing_note2,
          driver_id: load.driver_id,
          driver_name: load.drivers ? `${load.drivers.first_name} ${load.drivers.last_name}` : dispatchDriver ? `${dispatchDriver.first_name} ${dispatchDriver.last_name}` : 'Unknown',
          driver_pay: driverPay,
          dispatch_id: dispatchId,
          settlement_net_pay: driverSettlement?.net_pay || null,
          week_start: weekStart.toISOString(),
          month_start: monthStart.toISOString(),
          week_number: weekNumber,
          year: year,
          in_excel: loadsInExcelSet.has(load.id),
          excel_returned: load.excel_returned || false,
          // Carrier text first, then the dispatch's driver company, then the
          // load's OWN driver company. That last hop matters: a load with
          // driver_id set but no dispatch row yet has no other way to name a
          // carrier, and a null carrier matches no tab, so it used to be
          // invisible on every one of them.
          carrier: load.carrier || embeddedCompanyName(dispatchDriver) || embeddedCompanyName(load.drivers) || null,
        };
      });

      setLoads(processedLoads);
    } catch (error) {
      console.error('Error loading tabela data:', error);
    } finally {
      setLoading(false);
    }
  }

  function getWeekStart(date: Date): Date {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    // Set to midnight to ensure consistent grouping
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function getMonthStart(date: Date): Date {
    const d = new Date(date);
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function getWeekNumber(date: Date): number {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  }

  function formatMonthRange(monthStart: string): string {
    const date = new Date(monthStart);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }

  function toggleSection(sectionKey: string) {
    setCollapsedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionKey)) {
        newSet.delete(sectionKey);
      } else {
        newSet.add(sectionKey);
      }
      return newSet;
    });
  }

  function startEditingNote(loadId: string, currentNote: string | null) {
    setEditingLoadId(loadId);
    setEditingNote(currentNote || '');
  }

  function cancelEditingNote() {
    setEditingLoadId(null);
    setEditingNote('');
  }

  async function saveNote(loadId: string) {
    try {
      setSavingLoadId(loadId);
      
      const { error } = await supabase
        .from('loads')
        .update({ billing_note: editingNote })
        .eq('id', loadId);

      if (error) throw error;

      // Update local state
      setLoads(prevLoads => 
        prevLoads.map(load => 
          load.id === loadId 
            ? { ...load, notes: editingNote }
            : load
        )
      );

      setEditingLoadId(null);
      setEditingNote('');
    } catch (error) {
      console.error('Error saving note:', error);
      alert('Failed to save note. Please try again.');
    } finally {
      setSavingLoadId(null);
    }
  }

  function startEditingDriverPay(loadId: string, currentDriverPay: number | null) {
    setEditingDriverPayId(loadId);
    setEditingDriverPay(currentDriverPay?.toString() || '');
  }

  function cancelEditingDriverPay() {
    setEditingDriverPayId(null);
    setEditingDriverPay('');
  }

  async function saveDriverPay(loadId: string) {
    try {
      setSavingDriverPayId(loadId);
      
      const driverPayValue = parseFloat(editingDriverPay) || 0;
      
      // Find the dispatch_id for this load
      const load = loads.find(l => l.id === loadId);
      if (!load?.dispatch_id) {
        alert('No dispatch found for this load. Please create a dispatch first.');
        return;
      }
      
      const { error } = await supabase
        .from('dispatches')
        .update({ driver_pay: driverPayValue })
        .eq('id', load.dispatch_id);

      if (error) throw error;

      // Update local state
      setLoads(prevLoads => 
        prevLoads.map(l => 
          l.id === loadId 
            ? { ...l, driver_pay: driverPayValue }
            : l
        )
      );

      setEditingDriverPayId(null);
      setEditingDriverPay('');
    } catch (error) {
      console.error('Error saving driver pay:', error);
      alert('Failed to save driver pay. Please try again.');
    } finally {
      setSavingDriverPayId(null);
    }
  }

  function startEditingNote2(loadId: string, currentNote2: string | null) {
    setEditingNote2Id(loadId);
    setEditingNote2(currentNote2 || '');
  }

  function cancelEditingNote2() {
    setEditingNote2Id(null);
    setEditingNote2('');
  }

  async function saveNote2(loadId: string) {
    try {
      setSavingNote2Id(loadId);
      
      const { error } = await supabase
        .from('loads')
        .update({ billing_note2: editingNote2 })
        .eq('id', loadId);

      if (error) throw error;

      // Update local state
      setLoads(prevLoads => 
        prevLoads.map(load => 
          load.id === loadId 
            ? { ...load, notes2: editingNote2 }
            : load
        )
      );

      setEditingNote2Id(null);
      setEditingNote2('');
    } catch (error) {
      console.error('Error saving note 2:', error);
      alert('Failed to save note 2. Please try again.');
    } finally {
      setSavingNote2Id(null);
    }
  }

  function formatWeekRange(weekStart: string): string {
    const start = new Date(weekStart);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }

  const companyForCarrier = (carrier?: string | null) => {
    const key = normalizeCarrierKey(carrier);
    if (!key) return undefined;
    return companies.find(
      (c) => normalizeCarrierKey(c.name) === key || normalizeCarrierKey(c.code) === key
    );
  };

  // Does this load belong under the carrier tab currently selected? Loads that
  // resolve to no company live in the Unassigned bucket rather than nowhere.
  const loadMatchesCarrierFilter = (load: LoadRow) => {
    const company = companyForCarrier(load.carrier);
    if (carrierFilter === UNASSIGNED_CARRIER) return !company;
    return !!company && normalizeCarrierKey(company.name) === normalizeCarrierKey(carrierFilter);
  };
  const isUnassignedCarrier = carrierFilter === UNASSIGNED_CARRIER;

  const matchesNonCarrierFilters = (load: LoadRow) => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      load.driver_name.toLowerCase().includes(searchLower) ||
      load.load_number.toLowerCase().includes(searchLower) ||
      load.customer_name?.toLowerCase().includes(searchLower) ||
      load.invoice_number?.toLowerCase().includes(searchLower) ||
      load.notes?.toLowerCase().includes(searchLower) ||
      load.notes2?.toLowerCase().includes(searchLower) ||
      load.total_rate.toString().includes(searchLower) ||
      load.driver_pay?.toString().includes(searchLower) ||
      load.settlement_net_pay?.toString().includes(searchLower) ||
      (load.pickup_date && new Date(load.pickup_date).toLocaleDateString('en-US', { 
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      }).toLowerCase().includes(searchLower));
    
    const matchesWeek = selectedWeek === 'all' || load.week_start === selectedWeek;
    
    return matchesSearch && matchesWeek;
  };

  // Rows the carrier strip is choosing between: what the other active filters
  // (search, week/month) keep, tallied before the carrier filter narrows it.
  // An empty tab then reads as "they are under another carrier" instead of
  // "the screen is broken".
  const carrierTallyRows = loads.filter(matchesNonCarrierFilters);
  const carrierCountsByCompanyId = new Map<string, number>();
  let unassignedCarrierCount = 0;
  carrierTallyRows.forEach((load) => {
    const company = companyForCarrier(load.carrier);
    if (company) {
      carrierCountsByCompanyId.set(company.id, (carrierCountsByCompanyId.get(company.id) || 0) + 1);
    } else {
      unassignedCarrierCount += 1;
    }
  });

  const filteredLoads = carrierTallyRows.filter(loadMatchesCarrierFilter);

  // Group by week or month first, then by driver within each period
  const periodKey = groupBy === 'week' ? 'week_start' : 'month_start';
  const groupedByPeriod = filteredLoads.reduce((acc, load) => {
    const key = load[periodKey];
    if (!acc[key]) {
      acc[key] = {};
    }
    if (!acc[key][load.driver_id]) {
      acc[key][load.driver_id] = {
        driver_id: load.driver_id,
        driver_name: load.driver_name,
        loads: []
      };
    }
    acc[key][load.driver_id].loads.push(load);
    return acc;
  }, {} as Record<string, Record<string, { driver_id: string; driver_name: string; loads: LoadRow[] }>>);

  // Sort periods (most recent first) and drivers within each period
  const sortedPeriods = Object.entries(groupedByPeriod)
    .sort(([periodA], [periodB]) => new Date(periodB).getTime() - new Date(periodA).getTime())
    .map(([periodStart, drivers]) => ({
      periodStart,
      drivers: Object.values(drivers).sort((a, b) => a.driver_name.localeCompare(b.driver_name)),
      totalLoads: Object.values(drivers).reduce((sum, driver) => sum + driver.loads.length, 0)
    }));

  const uniqueWeeks = Array.from(new Set(loads.map(l => l.week_start)))
    .sort((a, b) => new Date(b).getTime() - new Date(a).getTime())
    .slice(0, 12);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search by driver, load, broker, or invoice..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        <div className="flex items-center gap-2 bg-slate-100 rounded-lg p-1">
          <button
            onClick={() => setGroupBy('week')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              groupBy === 'week'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Week
          </button>
          <button
            onClick={() => setGroupBy('month')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              groupBy === 'month'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Month
          </button>
        </div>

        <div className="relative min-w-[280px]">
          <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <select
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none bg-white"
          >
            <option value="all">All {groupBy === 'week' ? 'Weeks' : 'Months'}</option>
            {uniqueWeeks.map(week => (
              <option key={week} value={week}>
                {groupBy === 'week' ? formatWeekRange(week) : formatMonthRange(week)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Carrier Filter Tabs */}
      <div className="bg-white border border-slate-300 rounded-lg overflow-hidden">
        <div className="flex border-b border-slate-300">
          {companies.map((company, index) => {
            const colors = ['#8A0115', '#10B981', '#F97316', '#3B82F6', '#8B5CF6'];
            const bgColor = colors[index % colors.length];
            const isSelected = !isUnassignedCarrier && normalizeCarrierKey(company.name) === normalizeCarrierKey(carrierFilter);
            return (
              <button
                key={company.id}
                onClick={() => setCarrierFilter(company.name)}
                className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                  isSelected
                    ? 'text-white'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
                }`}
                style={isSelected ? { backgroundColor: bgColor } : {}}
              >
                {company.name}
                <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                  isSelected ? 'bg-white/25 text-white' : 'bg-slate-200 text-slate-700'
                }`}>
                  {carrierCountsByCompanyId.get(company.id) || 0}
                </span>
              </button>
            );
          })}
          {/* Loads whose carrier matches no company are unreachable from every
              other tab, so give them one of their own. */}
          {companies.length > 0 && (unassignedCarrierCount > 0 || isUnassignedCarrier) && (
            <button
              onClick={() => setCarrierFilter(UNASSIGNED_CARRIER)}
              title="Loads whose carrier doesn't match any active company"
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                isUnassignedCarrier
                  ? 'text-white'
                  : 'bg-slate-50 text-slate-600 hover:bg-slate-100'
              }`}
              style={isUnassignedCarrier ? { backgroundColor: '#D97706' } : {}}
            >
              Unassigned
              <span className={`ml-2 px-1.5 py-0.5 rounded-full text-xs font-semibold ${
                isUnassignedCarrier ? 'bg-white/25 text-white' : 'bg-amber-100 text-amber-800'
              }`}>
                {unassignedCarrierCount}
              </span>
            </button>
          )}
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-start gap-3">
            <div className="bg-yellow-100 w-6 h-6 rounded border border-yellow-300 flex-shrink-0 mt-0.5"></div>
            <div>
              <p className="text-sm font-medium text-slate-900">Yellow</p>
              <p className="text-sm text-slate-600">Invoice number generated</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="bg-green-100 w-6 h-6 rounded border border-green-300 flex-shrink-0 mt-0.5"></div>
            <div>
              <p className="text-sm font-medium text-slate-900">Green</p>
              <p className="text-sm text-slate-600">Included in Excel export</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="bg-red-100 w-6 h-6 rounded border border-red-300 flex-shrink-0 mt-0.5"></div>
            <div>
              <p className="text-sm font-medium text-slate-900">Red</p>
              <p className="text-sm text-slate-600">Returned from Excel</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-sm border border-slate-300 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-slate-100">
                <th className="px-3 py-2 text-right text-xs font-bold text-slate-700 border border-slate-300">PLATE</th>
                <th className="px-3 py-2 text-left text-xs font-bold text-slate-700 border border-slate-300">DRIVER</th>
                <th className="px-3 py-2 text-left text-xs font-bold text-slate-700 border border-slate-300">CARRIER</th>
                <th className="px-3 py-2 text-left text-xs font-bold text-slate-700 border border-slate-300">INVOICE #</th>
                <th className="px-3 py-2 text-left text-xs font-bold text-slate-700 border border-slate-300">PICK UP</th>
                <th className="px-3 py-2 text-left text-xs font-bold text-slate-700 border border-slate-300">LOAD #</th>
                <th className="px-3 py-2 text-left text-xs font-bold text-slate-700 border border-slate-300">BROKER</th>
                <th className="px-3 py-2 text-right text-xs font-bold text-slate-700 border border-slate-300">AMOUNT</th>
                <th className="px-3 py-2 text-left text-xs font-bold text-slate-700 border border-slate-300">NOTES</th>
                <th className="px-3 py-2 text-right text-xs font-bold text-slate-700 border border-slate-300">ZA VOZAČA</th>
                <th className="px-3 py-2 text-left text-xs font-bold text-slate-700 border border-slate-300">NOTE 2</th>
              </tr>
            </thead>
            <tbody>
              {sortedPeriods.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-3 py-8 text-center text-slate-500 border border-slate-300">
                    No loads found
                  </td>
                </tr>
              ) : (
                sortedPeriods.map((period) => {
                  const isCollapsed = collapsedSections.has(period.periodStart);
                  return (
                  <Fragment key={`period-${period.periodStart}`}>
                    <tr className="bg-slate-200">
                      <td colSpan={11} className="px-3 py-2 border border-slate-300">
                        <div className="flex items-center justify-between">
                          <button
                            onClick={() => toggleSection(period.periodStart)}
                            className="flex items-center gap-2 hover:text-slate-900 transition-colors"
                          >
                            {isCollapsed ? (
                              <ChevronRight className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                            <span className="font-bold text-slate-800 text-sm">
                              {groupBy === 'week' ? `Week of ${formatWeekRange(period.periodStart)}` : formatMonthRange(period.periodStart)}
                            </span>
                          </button>
                          <div className="flex items-center gap-6">
                            {isCollapsed && (
                              <>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-500 uppercase">Plate:</span>
                                  <span className="text-sm font-bold text-green-600">
                                    ${period.drivers.reduce((sum, driver) => 
                                      sum + driver.loads.reduce((driverSum, load) => 
                                        driverSum + (load.settlement_net_pay || 0), 0), 0)
                                      .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-500 uppercase">Amount:</span>
                                  <span className="text-sm font-bold text-slate-800">
                                    ${period.drivers.reduce((sum, driver) => 
                                      sum + driver.loads.reduce((driverSum, load) => 
                                        driverSum + (load.total_rate || 0), 0), 0)
                                      .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-slate-500 uppercase">Za Vozača:</span>
                                  <span className="text-sm font-bold text-blue-600">
                                    ${period.drivers.reduce((sum, driver) => 
                                      sum + driver.loads.reduce((driverSum, load) => 
                                        driverSum + (load.driver_pay || 0), 0), 0)
                                      .toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                              </>
                            )}
                            <span className="text-xs text-slate-600 font-semibold">
                              {period.totalLoads} load{period.totalLoads !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </td>
                    </tr>
                    {!isCollapsed && (
                      <>
                        {period.drivers.map((driver) => 
                          driver.loads.map((load, loadIndex) => {
                            // Determine row color based on status
                            let bgColor = 'bg-white';
                            if (load.excel_returned) {
                              bgColor = 'bg-red-100'; // Red for returned invoices
                            } else if (load.in_excel) {
                              bgColor = 'bg-green-100'; // Green for Excel-generated
                            } else if (load.invoice_number) {
                              bgColor = 'bg-yellow-100'; // Yellow for invoiced
                            }
                            
                            return (
                            <tr 
                              key={load.id} 
                              className={`hover:bg-blue-50 ${bgColor}`}
                            >
                          <td className="px-3 py-2 text-right text-slate-900 border border-slate-300 text-sm">
                            {load.settlement_net_pay ? (
                              <span className="font-semibold text-green-700">
                                ${Number(load.settlement_net_pay).toLocaleString(undefined, { 
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2 
                                })}
                              </span>
                            ) : (
                              <span className="text-slate-400"></span>
                            )}
                          </td>
                          <td className="px-3 py-2 border border-slate-300">
                            {loadIndex === 0 ? (
                              <span className="font-semibold text-slate-900 text-sm">{driver.driver_name}</span>
                            ) : (
                              <span className="text-slate-400 text-sm"></span>
                            )}
                          </td>
                          <td className="px-3 py-2 border border-slate-300">
                            <span className="text-sm text-slate-700 font-medium">
                              {load.carrier || '-'}
                            </span>
                          </td>
                          <td className="px-3 py-2 border border-slate-300">
                            {load.invoice_number ? (
                              <a
                                href={`/invoices?search=${encodeURIComponent(load.invoice_number)}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.history.pushState({}, '', `/invoices?search=${encodeURIComponent(load.invoice_number!)}`);
                                  window.dispatchEvent(new Event('urlchange'));
                                  window.location.reload();
                                }}
                                className="text-sm font-semibold text-blue-700 hover:text-blue-900 hover:underline cursor-pointer"
                                title={`View invoice ${load.invoice_number}`}
                              >
                                {load.invoice_number}
                              </a>
                            ) : (
                              <span className="text-sm text-slate-400"></span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-900 border border-slate-300 text-sm">
                            {load.pickup_date 
                              ? (() => {
                                  const dateStr = load.pickup_date.split('T')[0];
                                  const [y, m, d] = dateStr.split('-');
                                  return new Date(parseInt(y), parseInt(m) - 1, parseInt(d)).toLocaleDateString('en-US', { 
                                    day: 'numeric',
                                    month: 'short'
                                  });
                                })()
                              : ''}
                          </td>
                          <td className="px-3 py-2 border border-slate-300">
                            <a
                              href={`/loads/${load.id}`}
                              onClick={(e) => {
                                e.preventDefault();
                                window.history.pushState({}, '', `/loads/${load.id}`);
                                window.dispatchEvent(new Event('urlchange'));
                                window.location.reload();
                              }}
                              className="text-sm text-blue-700 hover:text-blue-900 hover:underline cursor-pointer font-medium"
                              title={`View load ${load.load_number}`}
                            >
                              {load.load_number}
                            </a>
                          </td>
                          <td className="px-3 py-2 text-slate-900 border border-slate-300 text-sm">
                            {load.customer_name || ''}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-slate-900 border border-slate-300 text-sm">
                            ${Number(load.total_rate).toLocaleString(undefined, { 
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2 
                            })}
                          </td>
                          <td className="px-3 py-2 text-slate-700 border border-slate-300 text-xs relative">
                            {editingLoadId === load.id ? (
                              <div className="flex items-start gap-2">
                                <textarea
                                  value={editingNote}
                                  onChange={(e) => setEditingNote(e.target.value)}
                                  className="flex-1 px-2 py-1 text-xs border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[60px]"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      cancelEditingNote();
                                    }
                                  }}
                                />
                                <div className="flex flex-col gap-1">
                                  <button
                                    onClick={() => saveNote(load.id)}
                                    disabled={savingLoadId === load.id}
                                    className="p-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                    title="Save"
                                  >
                                    <Check className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={cancelEditingNote}
                                    disabled={savingLoadId === load.id}
                                    className="p-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                    title="Cancel"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div
                                onClick={() => startEditingNote(load.id, load.notes)}
                                className="cursor-pointer hover:bg-slate-100 rounded px-1 py-1 min-h-[20px]"
                                title="Click to edit note"
                              >
                                {load.notes || <span className="text-slate-400 italic">Click to add note...</span>}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right border border-slate-300 text-sm">
                            {editingDriverPayId === load.id ? (
                              <div className="flex items-center justify-end gap-2">
                                <input
                                  type="number"
                                  step="0.01"
                                  value={editingDriverPay}
                                  onChange={(e) => setEditingDriverPay(e.target.value)}
                                  className="w-24 px-2 py-1 text-sm text-right border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      cancelEditingDriverPay();
                                    } else if (e.key === 'Enter') {
                                      saveDriverPay(load.id);
                                    }
                                  }}
                                />
                                <button
                                  onClick={() => saveDriverPay(load.id)}
                                  disabled={savingDriverPayId === load.id}
                                  className="p-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                  title="Save"
                                >
                                  <Check className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={cancelEditingDriverPay}
                                  disabled={savingDriverPayId === load.id}
                                  className="p-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                  title="Cancel"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <div
                                onClick={() => startEditingDriverPay(load.id, load.driver_pay)}
                                className="cursor-pointer hover:bg-slate-100 rounded px-2 py-1 text-right"
                                title="Click to edit driver pay"
                              >
                                {load.driver_pay ? (
                                  <span className="font-semibold text-blue-700">
                                    ${Number(load.driver_pay).toLocaleString(undefined, { 
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2 
                                    })}
                                  </span>
                                ) : (
                                  <span className="text-slate-400 italic text-xs">Click to add...</span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-700 border border-slate-300 text-xs relative">
                            {editingNote2Id === load.id ? (
                              <div className="flex items-start gap-2">
                                <textarea
                                  value={editingNote2}
                                  onChange={(e) => setEditingNote2(e.target.value)}
                                  className="flex-1 px-2 py-1 text-xs border border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[60px]"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Escape') {
                                      cancelEditingNote2();
                                    }
                                  }}
                                />
                                <div className="flex flex-col gap-1">
                                  <button
                                    onClick={() => saveNote2(load.id)}
                                    disabled={savingNote2Id === load.id}
                                    className="p-1 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                                    title="Save"
                                  >
                                    <Check className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={cancelEditingNote2}
                                    disabled={savingNote2Id === load.id}
                                    className="p-1 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                                    title="Cancel"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div
                                onClick={() => startEditingNote2(load.id, load.notes2)}
                                className="cursor-pointer hover:bg-slate-100 rounded px-1 py-1 min-h-[20px]"
                                title="Click to edit note 2"
                              >
                                {load.notes2 || <span className="text-slate-400 italic">Click to add note...</span>}
                              </div>
                            )}
                          </td>
                            </tr>
                          );
                          })
                        )}
                        
                        {/* Total row for this period */}
                        <tr className="bg-blue-50 border-t-2 border-blue-400">
                          <td className="px-3 py-2 text-right border-r border-blue-200">
                            {(() => {
                              const totalSettlement = period.drivers.reduce((sum, driver) => 
                                sum + driver.loads.reduce((driverSum, load) => 
                                  driverSum + (load.settlement_net_pay || 0), 0), 0);
                              return totalSettlement > 0 ? (
                                <span className="text-green-600 font-bold text-sm">
                                  ${totalSettlement.toLocaleString(undefined, { 
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2 
                                  })}
                                </span>
                              ) : <span className="text-black text-xs">$0.00</span>;
                            })()}
                          </td>
                          <td colSpan={6} className="px-3 py-2 text-center border-r border-blue-200">
                            <span className="text-blue-900 font-semibold text-xs uppercase tracking-wider">
                              {groupBy === 'week' ? 'Week Total' : 'Month Total'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right border-r border-blue-200">
                            {(() => {
                              const totalAmount = period.drivers.reduce((sum, driver) => 
                                sum + driver.loads.reduce((driverSum, load) => 
                                  driverSum + (load.total_rate || 0), 0), 0);
                              return totalAmount > 0 ? (
                                <span className="text-slate-800 font-bold text-sm">
                                  ${totalAmount.toLocaleString(undefined, { 
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2 
                                  })}
                                </span>
                              ) : <span className="text-black text-xs">$0.00</span>;
                            })()}
                          </td>
                          <td className="px-3 py-2 border-r border-blue-200"></td>
                          <td className="px-3 py-2 text-right border-r border-blue-200">
                            {(() => {
                              const totalDriverPay = period.drivers.reduce((sum, driver) => 
                                sum + driver.loads.reduce((driverSum, load) => 
                                  driverSum + (load.driver_pay || 0), 0), 0);
                              return totalDriverPay > 0 ? (
                                <span className="text-blue-600 font-bold text-sm">
                                  ${totalDriverPay.toLocaleString(undefined, { 
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2 
                                  })}
                                </span>
                              ) : <span className="text-black text-xs">$0.00</span>;
                            })()}
                          </td>
                          <td className="px-3 py-2"></td>
                        </tr>
                      </>
                    )}
                  </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
